import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, Lock, User } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { SectionTitle, Panel } from "@/components/platform/PortalLayout";

const SECTIONS = [
  {
    title: "Personal", fields: [
      { key: "first_name", label: "First Name" }, { key: "last_name", label: "Last Name" },
      { key: "phone", label: "Phone Number" }, { key: "city", label: "City" }, { key: "state", label: "State" },
      { key: "dob", label: "Date of Birth", type: "date" },
    ],
  },
  {
    title: "Athletics", fields: [
      { key: "sport", label: "Primary Sport" }, { key: "position", label: "Primary Position / Event" },
      { key: "secondary_position", label: "Secondary Position / Event" }, { key: "school", label: "School" },
      { key: "grad_year", label: "Graduation Year" }, { key: "grade_level", label: "Grade Level" },
      { key: "team_club", label: "Team or Club" },
    ],
  },
  {
    title: "Goals", fields: [
      { key: "primary_goal", label: "Primary Goal" }, { key: "secondary_goal", label: "Secondary Goal" },
      { key: "short_term_goal", label: "Short-Term Goal" }, { key: "long_term_goal", label: "Long-Term Goal" },
      { key: "upcoming_season", label: "Upcoming Season / Competition" },
    ],
  },
  {
    title: "More", fields: [
      { key: "prs", label: "Personal Records" }, { key: "additional_info", label: "Additional Information" },
    ],
  },
];
const GUARDIAN = [
  { key: "parent_name", label: "Parent / Guardian Name" }, { key: "parent_relationship", label: "Relationship" },
  { key: "parent_email", label: "Parent Email" }, { key: "parent_phone", label: "Parent Phone" },
  { key: "preferred_contact", label: "Preferred Contact Method" },
];
const READONLY = [
  { key: "athlete_type", label: "Athlete Type" }, { key: "training_phase", label: "Training Phase" },
  { key: "height", label: "Height" }, { key: "weight", label: "Weight" },
];
const ALL_KEYS = [...SECTIONS.flatMap((s) => s.fields.map((f) => f.key)), ...GUARDIAN.map((g) => g.key), "photo_url"];

const inputCls = "w-full bg-[#0A0A0A] border border-white/15 focus:border-white outline-none px-3 py-2.5 text-sm transition-colors";
const labelCls = "font-mono-accent text-[10px] tracking-[0.2em] uppercase text-white/40 block mb-1.5";

export default function MyProfile({ athlete, accountEmail, onSaved }) {
  const [form, setForm] = useState(() => ALL_KEYS.reduce((acc, k) => ({ ...acc, [k]: athlete?.[k] || "" }), {}));
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/athletes/${athlete.id}`, form);
      toast.success("Profile updated.");
      onSaved?.();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setSaving(false); }
  };

  return (
    <div data-testid="my-profile" className="space-y-8">
      <SectionTitle>My Athletic Profile</SectionTitle>
      <div className="flex items-center gap-4">
        <div className="w-20 h-20 rounded-full overflow-hidden bg-[#1a1a1a] border border-white/10 flex items-center justify-center shrink-0">
          {form.photo_url ? <img src={form.photo_url} alt={athlete.name} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} /> : <User size={30} className="text-white/30" />}
        </div>
        <div className="flex-1">
          <p className="font-display font-black uppercase tracking-tight text-2xl">{athlete.name}</p>
          <label className={`${labelCls} mt-2`}>Profile Photo URL</label>
          <input data-testid="profile-photo_url" value={form.photo_url} onChange={(e) => set("photo_url", e.target.value)} className={inputCls} />
        </div>
      </div>

      {SECTIONS.map((sec) => (
        <Panel key={sec.title}>
          <p className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-white/40 mb-4">{sec.title}</p>
          <div className="grid sm:grid-cols-2 gap-4">
            {sec.fields.map((f) => (
              <div key={f.key} className={f.key === "additional_info" || f.key === "prs" ? "sm:col-span-2" : ""}>
                <label className={labelCls}>{f.label}</label>
                <input data-testid={`profile-${f.key}`} type={f.type || "text"} value={form[f.key]} onChange={(e) => set(f.key, e.target.value)} className={inputCls} />
              </div>
            ))}
          </div>
        </Panel>
      ))}

      <Panel data-testid="profile-guardian">
        <p className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-white/40 mb-1">Parent / Guardian</p>
        <p className="text-white/35 text-xs mb-4">Required for athletes under 18. At least one contact must remain on file.</p>
        <div className="grid sm:grid-cols-2 gap-4">
          {GUARDIAN.map((f) => (
            <div key={f.key}>
              <label className={labelCls}>{f.label}</label>
              <input data-testid={`profile-${f.key}`} value={form[f.key]} onChange={(e) => set(f.key, e.target.value)} className={inputCls} />
            </div>
          ))}
        </div>
        <div className="grid sm:grid-cols-2 gap-px bg-white/10 border border-white/10 mt-4">
          <div className="bg-[#0A0A0A] p-3"><p className="text-white/40 text-[10px] uppercase tracking-widest">Waiver Status</p><p className="font-bold mt-1 text-sm">{athlete.waiver_status || "Not on file"}</p></div>
          <div className="bg-[#0A0A0A] p-3"><p className="text-white/40 text-[10px] uppercase tracking-widest">Consent Status</p><p className="font-bold mt-1 text-sm">{athlete.consent_status || "Not on file"}</p></div>
        </div>
      </Panel>

      <button data-testid="profile-save" onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-white text-black font-bold uppercase tracking-widest text-xs px-6 py-3 hover:bg-white/85 transition-colors disabled:opacity-60">
        {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save Changes
      </button>

      <div>
        <p className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-white/40 mb-3 flex items-center gap-2"><Lock size={12} /> Managed by your coach (view-only)</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
          <div className="bg-[#0A0A0A] p-4"><p className="text-white/40 text-[10px] uppercase tracking-widest">Email</p><p className="font-bold mt-1 text-sm break-all">{accountEmail || "—"}</p></div>
          {READONLY.map((f) => (
            <div key={f.key} className="bg-[#0A0A0A] p-4"><p className="text-white/40 text-[10px] uppercase tracking-widest">{f.label}</p><p className="font-bold mt-1 text-sm">{athlete[f.key] || "—"}</p></div>
          ))}
        </div>
        <p className="text-white/30 text-xs mt-2">Change your email in Account Settings. Official evaluation results and administrator notes are managed by your coach.</p>
      </div>
    </div>
  );
}
